import {
  DeprecatedDropdown,
  DeprecatedDropdownButton,
  DeprecatedDropdownItem,
  DeprecatedDropdownMenu,
} from "@deere/fuel-react";
import { Box, Radio, Typography } from "@mui/material";
import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import FormattedAddress from "./FormattedAddress";
import { SavedAddress } from "../../../../api/orders/orderInterfaces";
import { OrderContext } from "../../../../context/OrderContext/OrderContext";
import { Address } from "../../types";
import {
  convertNewAddressToOldAddressType,
  convertOldAddressToNewAddressType,
} from "../../utils";

const AddressSelectDropdown = ({
  updateAddress,
  currentAddress,
  disabled = false,
}: {
  updateAddress?: (res: {
    shipToAddress: SavedAddress;
    isShipToAddressOverridden: boolean;
    isDealerAddressChanged?: boolean;
  }) => void;
  currentAddress?: Address;
  disabled?: boolean;
}) => {
  const { getShipToAddressList, getDefaultShipToAddress } =
    useContext(OrderContext);
  const { t } = useTranslation("orders");

  const addressSelected = (address: Address) => {
    if (!updateAddress || isSame(address, currentAddress)) return;

    const defaultAddress = getDefaultShipToAddress();
    const isOverridden = !isSame(address, defaultAddress);

    updateAddress({
      shipToAddress: convertNewAddressToOldAddressType(address)!,
      isShipToAddressOverridden: isOverridden,
      isDealerAddressChanged: true,
    });
  };

  const getAddressList = (): Address[] => {
    const defaultAddress = getDefaultShipToAddress();
    const addressList = getShipToAddressList()
      .map((address) => convertOldAddressToNewAddressType(address))
      .filter((address) => address !== undefined) as Address[];
    if (
      currentAddress &&
      !addressList.some((address) => isSame(address, currentAddress))
    ) {
      addressList.push(currentAddress);
    }
    if (
      defaultAddress &&
      !addressList.some((address) => isSame(address, defaultAddress))
    ) {
      addressList.push(defaultAddress);
    }
    return addressList;
  };

  const isSame = (addressOne: Address, addressTwo?: Address) => {
    if (!addressOne || !addressTwo) return false;
    const properties: (keyof Address)[] = [
      "addressLine1",
      "addressLine2",
      "city",
      "country",
      "name",
      "phoneNumber",
      "postalCode",
      "state",
    ];

    return !properties.some(
      (prop) =>
        addressOne[prop]?.trim()?.toUpperCase() !==
        addressTwo[prop]?.trim()?.toUpperCase()
    );
  };

  const getAddressStringRepresentation = (address: Address) =>
    `${address.city}, ${address.country}`;

  return (
    <DeprecatedDropdown>
      <DeprecatedDropdownButton
        fullWidth
        data-testid="addressSelectDropdownButton"
        disabled={disabled}
      >
        <Typography textAlign="left" width="100%">
          {currentAddress
            ? getAddressStringRepresentation(currentAddress)
            : t("addressSelectDropdown.button.label")}
        </Typography>
      </DeprecatedDropdownButton>
      <DeprecatedDropdownMenu
        style={{
          maxHeight: "20rem",
          overflowY: "auto",
          maxWidth: "24rem",
          width: "24rem",
        }}
        data-testid="addressSelectDropdownMenu"
      >
        {getAddressList().map((address, index) => (
          <DeprecatedDropdownItem
            key={`ship-to-address-${address.city}-${address.country}`}
            onClick={() => {
              addressSelected(address);
            }}
          >
            <Box
              style={{
                display: "flex",
                flexDirection: "row",
                gap: 2,
              }}
            >
              <Radio
                checked={isSame(address, currentAddress)}
                style={{ paddingTop: "1.5rem" }}
              />
              <FormattedAddress
                address={address}
                identifier={`ship-to-address-${index}`}
              />
            </Box>
          </DeprecatedDropdownItem>
        ))}
      </DeprecatedDropdownMenu>
    </DeprecatedDropdown>
  );
};

export default AddressSelectDropdown;
